import {
  addDays,
  addMilliseconds,
  differenceInDays,
  differenceInMilliseconds,
  startOfDay,
} from 'date-fns';
import React, { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getDayTimeInMillis } from '@lmt/unicorn-shared/app/helpers/date-helper';
import clsx from 'clsx';
import { Field } from '../../../Field';
import { DatePicker } from './DatePicker';
import { TimePicker } from './TimePicker';

import styles from './DateTimeIntervalPicker.module.scss';

export interface DateTimeInterval {
  startDate?: Date | null;
  startTimeInMills?: number | null;
  endDate?: Date | null;
  endTimeInMills?: number | null;
}
export function stringifyDateTimeIntervalForMemoization(
  interval: DateTimeInterval,
) {
  if (!interval) return '';
  return `${interval.startDate?.getTime()}${
    interval.startTimeInMills
  }${interval.endDate?.getTime()}${interval.endTimeInMills}`;
}

interface DateTimeIntervalPickerProps {
  mode?: 'single-line' | 'two-lines';
  minDateTime?: Date;
  value: DateTimeInterval;
  onIntervalChanged: (newInterval: DateTimeInterval) => void;
  className?: string;
  datePickerClassName?: string;
  timePickerClassName?: string;
  startDateError?: boolean;
  startDateErrorText?: string;
  startTimeError?: boolean;
  startTimeErrorText?: string;
  endDateError?: boolean;
  endDateErrorText?: string;
  endTimeError?: boolean;
  endTimeErrorText?: string;
  isDateOnly?: boolean;
}
const millisInADay = 24 * 60 * 60 * 1000;
export const DateTimeIntervalPicker: FC<DateTimeIntervalPickerProps> = (
  props,
) => {
  const {
    mode = 'single-line',
    minDateTime,
    value,
    onIntervalChanged,
    className,
    datePickerClassName,
    timePickerClassName,
    startDateError,
    startDateErrorText,
    startTimeError,
    startTimeErrorText,
    endDateError,
    endDateErrorText,
    endTimeError,
    endTimeErrorText,
    isDateOnly,
  } = props;

  const i18n = useTranslation();

  const sameStartAndEndDate = useMemo(
    () =>
      value?.startDate &&
      value?.endDate &&
      value.startDate.getTime() === value.endDate.getTime(),
    [value],
  );

  const onStartDateChange = useCallback(
    (newStartDate: Date | null) => {
      const startDate = value.startDate;
      let endDate = value?.endDate;

      if (newStartDate) {
        if (!endDate) {
          endDate = newStartDate;
        } else if (startDate && endDate) {
          const diffInDays = differenceInDays(newStartDate, startDate);
          endDate = addDays(endDate, diffInDays);
        }
      }
      const newInterval: DateTimeInterval = {
        startDate: newStartDate,
        startTimeInMills: value?.startTimeInMills,
        endDate,
        endTimeInMills: value?.endTimeInMills,
      };
      onIntervalChanged?.(newInterval);
    },
    [value, onIntervalChanged],
  );

  const onStartTimeChange = useCallback(
    (newStartTimeInMilliseconds: number | null, isInvalid: boolean) => {
      let endTimeInMilliseconds = value?.endTimeInMills;
      let endDate = value?.endDate;
      const startTimeInMilliseconds = value?.startTimeInMills;
      if (
        endTimeInMilliseconds !== null &&
        endTimeInMilliseconds !== undefined &&
        newStartTimeInMilliseconds !== null &&
        startTimeInMilliseconds !== null &&
        startTimeInMilliseconds !== undefined
      ) {
        // we preserve interval length when we change the start time
        const durationInMilliseconds = differenceInMilliseconds(
          endTimeInMilliseconds,
          startTimeInMilliseconds,
        );
        endTimeInMilliseconds =
          newStartTimeInMilliseconds + durationInMilliseconds;
        if (endTimeInMilliseconds >= millisInADay) {
          endTimeInMilliseconds = endTimeInMilliseconds - millisInADay;
          endDate = addMilliseconds(endDate!, millisInADay);
        }
      }
      if (newStartTimeInMilliseconds === null && isInvalid) {
        //reset to previous value if new one is invalid
        newStartTimeInMilliseconds =
          startTimeInMilliseconds !== undefined
            ? startTimeInMilliseconds
            : null;
      }
      const newInterval: DateTimeInterval = {
        startDate: value?.startDate,
        startTimeInMills: newStartTimeInMilliseconds,
        endDate: endDate,
        endTimeInMills: endTimeInMilliseconds,
      };
      onIntervalChanged?.(newInterval);
    },
    [value, onIntervalChanged],
  );

  const onEndDateChange = useCallback(
    (newEndDate: Date | null) => {
      let startDate = value?.startDate;
      const startTime = value?.startTimeInMills;
      let endTime = value?.endTimeInMills;
      if (newEndDate && (!startDate || newEndDate < startDate)) {
        startDate = newEndDate;
      }
      if (
        startDate &&
        newEndDate &&
        startDate?.getTime() === newEndDate?.getTime() &&
        startTime !== null &&
        startTime !== undefined &&
        endTime !== null &&
        endTime !== undefined &&
        startTime > endTime
      ) {
        endTime = startTime;
      }
      const newInterval: DateTimeInterval = {
        startDate: startDate,
        startTimeInMills: startTime,
        endDate: newEndDate,
        endTimeInMills: endTime,
      };
      onIntervalChanged?.(newInterval);
    },
    [value, onIntervalChanged],
  );

  const onEndTimeChange = useCallback(
    (newEndTime: number | null, isInvalid: boolean) => {
      const startTime = value?.startTimeInMills;
      if (
        sameStartAndEndDate &&
        startTime !== null &&
        startTime !== undefined &&
        newEndTime !== null &&
        newEndTime < startTime
      ) {
        newEndTime = startTime;
      }
      if (newEndTime === null && isInvalid) {
        const endTime = value?.endTimeInMills;
        newEndTime = endTime !== undefined ? endTime : null;
      }
      const newInterval: DateTimeInterval = {
        startDate: value?.startDate,
        startTimeInMills: value?.startTimeInMills,
        endDate: value?.endDate,
        endTimeInMills: newEndTime,
      };
      onIntervalChanged?.(newInterval);
    },
    [value, onIntervalChanged, sameStartAndEndDate],
  );

  const isSingleLine = mode === 'single-line';

  const minDate = useMemo(
    () => minDateTime && startOfDay(minDateTime),
    [minDateTime],
  );
  const minTime = useMemo(() => getDayTimeInMillis(minDateTime), [minDateTime]);
  return (
    <div
      className={clsx(
        isSingleLine ? styles.singleLineContainer : styles.twoLinesContainer,
        className,
      )}
    >
      <div className={styles.dateTimeBlock}>
        <Field title={i18n.t('uikit.inputs.date_input.starts')}>
          <DatePicker
            minDate={minDate}
            containerClassName={datePickerClassName}
            value={value?.startDate || null}
            onDateChanged={onStartDateChange}
            error={startDateError}
            errorText={startDateErrorText}
          />
        </Field>
        {!isDateOnly && (
          <TimePicker
            testId={'startTime'}
            minTimeInMills={
              minDate !== undefined &&
              minDate.getTime() === value?.startDate?.getTime()
                ? minTime
                : undefined
            }
            timeInMills={value?.startTimeInMills}
            error={startTimeError}
            errorText={startTimeErrorText}
            onTimeChanged={onStartTimeChange}
            className={clsx(
              isSingleLine
                ? styles.timePickerOneLine
                : styles.timePickerTwoLines,
              timePickerClassName,
            )}
          />
        )}
      </div>
      {isSingleLine ? <div className={styles.timeSeparator}>—</div> : null}
      <div
        className={clsx(
          styles.dateTimeBlock,
          isSingleLine ? undefined : styles.dateTimeLowerBlock,
        )}
      >
        <Field title={i18n.t('uikit.inputs.date_input.ends')}>
          <DatePicker
            containerClassName={datePickerClassName}
            value={value?.endDate || null}
            minDate={value?.startDate || minDate}
            onDateChanged={onEndDateChange}
            error={endDateError}
            errorText={endDateErrorText}
          />
        </Field>
        {!isDateOnly && (
          <TimePicker
            testId={'endTime'}
            timeInMills={value?.endTimeInMills}
            minTimeInMills={
              sameStartAndEndDate && value?.startTimeInMills
                ? value.startTimeInMills
                : undefined
            }
            error={endTimeError}
            errorText={endTimeErrorText}
            onTimeChanged={onEndTimeChange}
            className={clsx(
              isSingleLine
                ? styles.timePickerOneLine
                : styles.timePickerTwoLines,
              timePickerClassName,
            )}
          />
        )}
      </div>
    </div>
  );
};
